/**
 * TK-38 việc 4 (lee 23/10) — Ô THÔNG TIN của mục đang chọn trên cây (trang Quản lý đèn): Tuyến · Tủ · Đèn.
 *   · Tuyến: số tủ, số đèn, đèn có điện, đèn sáng, sự cố đang mở, công suất đèn cộng lại (luật số 0: không số ⇒ "—").
 *   · Tủ: trực tuyến/mất kết nối (XÁM), thời gian hoạt động (lên mạng lúc · đã nối · giờ ngõ bật hôm nay THEO LỊCH tủ đang chạy),
 *     số đèn, kênh RF/PAN (khung chi tiết — được phép), card NEMA, "Mở trang tủ".
 *   · Đèn: Định danh (ID 4 hex · nhóm · công suất danh định · vị trí bản đồ) · Hoạt động (% · P · U · I mA · PF · GPS đèn) ·
 *     Cài đặt (tên) + đồ thị P và % 24 giờ / 7 ngày. Thêm / thay / xoá đèn: nút mờ (đợt E).
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { KIEU_GOI_Y, defsMien, toMien, useIdMien } from '../bieudo/Mien';
import { Badge, Button } from '../ui';
import { NHAN_BADGE, tenDen } from '../../lib/nemaNhan';
import { tuoiNgan, useLichSuDen, useNemaTu, useNhomTu, type KhoangLichSu, type TuCay, type TuyenCay } from '../../lib/nemaApi';
import { useSchedule } from '../../lib/queries';
import { iMa, pfHai, phutBatHomNay } from '../../lib/vanHanh';
import type { NgoLich } from '../../lib/gioCapDien';

const hex4 = (a: number) => a.toString(16).toUpperCase().padStart(4, '0');
const so = (v: number | null | undefined, dv = '') => (v == null ? '—' : `${v}${dv ? ` ${dv}` : ''}`);
const gioPhut = (ms: number) => { const p = Math.floor(ms / 60_000); return p < 60 ? `${p} phút` : `${Math.floor(p / 60)} giờ ${p % 60} phút`; };
const NHAN_HOST: Record<number, string> = { 0: 'Khởi động', 1: 'Chờ module RF', 2: 'Mạng đang lên', 3: 'Mạng tốt', 4: 'Đang quét' };

function O({ nhan, gia, xam = false }: { nhan: string; gia: string; xam?: boolean }) {
  return (
    <div className="min-w-0 rounded-nut border border-vien bg-nen-2 px-3 py-2">
      <small className="block truncate text-xs text-chu-2">{nhan}</small>
      <b className={`block truncate text-base font-semibold tabular-nums ${xam || gia === '—' ? 'text-chu-3' : 'text-chu-1'}`}>{gia}</b>
    </div>
  );
}
function Nhom({ ten, children }: { ten: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <h3 className="m-0 text-xs font-semibold uppercase tracking-wide text-chu-2">{ten}</h3>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">{children}</div>
    </div>
  );
}

export function OTuyen({ r }: { r: TuyenCay }) {
  const den = r.tu.reduce((n, t) => n + t.soDen, 0);
  const coP = r.tu.filter((t) => t.congSuatW !== null);
  return (
    <section className="flex flex-col gap-3 rounded-the border border-vien bg-nen-1 p-4" data-o="o-tuyen">
      <h2 className="m-0 text-lg font-semibold text-chu-1">{r.tenDayDu}</h2>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <O nhan="Số tủ" gia={String(r.tu.length)} />
        <O nhan="Số đèn" gia={String(den)} />
        <O nhan="Đèn có điện" gia={String(r.tu.reduce((n, t) => n + t.capDien, 0))} />
        <O nhan="Đèn sáng" gia={String(r.tu.reduce((n, t) => n + t.sang, 0))} />
        <O nhan="Sự cố đang mở" gia={String(r.tu.reduce((n, t) => n + t.vanDe.that, 0))} />
        <O nhan="Công suất đèn" gia={coP.length ? `${coP.reduce((n, t) => n + (t.congSuatW as number), 0)} W` : '—'} />
      </div>
    </section>
  );
}

export function OTu({ t }: { t: TuCay }) {
  const lichQ = useSchedule(t.id);
  const tuQ = useNemaTu(t.id);
  const lich = (lichQ.data?.dangChay?.outputs ?? null) as NgoLich[] | null;
  const mt = tuQ.data?.cabinet.matTroi ?? { sunsetMin: null, sunriseMin: null };
  const phut = phutBatHomNay(lich, mt, Date.now());
  const h = t.host;
  return (
    <section className="flex flex-col gap-3 rounded-the border border-vien bg-nen-1 p-4" data-o="o-tu">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="m-0 min-w-0 truncate text-lg font-semibold text-chu-1">{t.name}</h2>
        {t.online ? <Badge tone="green">Trực tuyến</Badge> : <Badge tone="slate">Mất kết nối</Badge>}
        {t.giaLap && <Badge tone="slate">GIẢ LẬP</Badge>}
        <Link to={`/tu/${t.id}`} className="ml-auto text-sm text-nhan hover:underline">Mở trang tủ</Link>
      </div>
      <Nhom ten="Thời gian hoạt động">
        <O nhan="Lên mạng lúc" gia={t.lucNoi ? new Date(t.lucNoi).toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : '—'} />
        <O nhan="Đã nối" gia={t.lucNoi ? gioPhut(Date.now() - new Date(t.lucNoi).getTime()) : '—'} />
        <O nhan="Giờ ngõ bật hôm nay (theo lịch)" gia={phut === null ? '—' : gioPhut(phut * 60_000)} />
      </Nhom>
      <Nhom ten="Đèn">
        <O nhan="Số đèn" gia={String(t.soDen)} />
        <O nhan="Đèn sáng" gia={t.online ? String(t.sang) : '—'} />
        <O nhan="Mạng liên kết đèn" gia={!h ? '—' : h.state === null ? '—' : NHAN_HOST[h.state] ?? '—'} xam={!t.online} />
        <O nhan="Kênh hoạt động" gia={so(h?.rf_channel)} />
        <O nhan="PAN" gia={h?.pan_id == null ? '—' : `0x${hex4(h.pan_id)}`} />
        <O nhan="Sự cố đang mở" gia={String(t.vanDe.that)} />
      </Nhom>
    </section>
  );
}

function DoThiDen({ cabinetId, addr }: { cabinetId: string; addr: number }) {
  const [khoang, setKhoang] = useState<KhoangLichSu>('24h');
  const q = useLichSuDen(cabinetId, addr, khoang);
  const idMien = useIdMien();   // KA-49 §3: hai lớp — P (cyan) và mức dim (tím, bậc)
  const ds = useMemo(() => (q.data?.rows ?? []).map((r) => ({ t: new Date(r.ts).getTime(), p: r.p_w, d: r.dim })), [q.data]);
  return (
    <div className="flex flex-col gap-2" data-o="do-thi-den">
      <div className="flex items-center gap-2">
        <h3 className="m-0 text-xs font-semibold uppercase tracking-wide text-chu-2">P (W) và %</h3>
        <div className="nhom-lom ml-auto inline-flex rounded-nut border border-vien p-0.5" role="group" aria-label="Khoảng">
          {(['24h', '7d'] as const).map((k) => (
            <button key={k} type="button" aria-pressed={khoang === k} onClick={() => setKhoang(k)} data-khoang={k}
              className={`rounded-nut px-2.5 py-1 text-xs ${khoang === k ? 'bg-nen-3 text-nhan' : 'text-chu-2 hover:text-chu-1'}`}>{k === '24h' ? '24 giờ' : '7 ngày'}</button>
          ))}
        </div>
      </div>
      <div className="h-48 rounded-nut border border-vien bg-nen-2 p-2">
        {ds.length >= 2 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={ds} margin={{ top: 4, right: 6, bottom: 0, left: -18 }}>
              {defsMien(idMien, 2, ['--nhan', '--tim'])}
              <XAxis dataKey="t" type="number" domain={['dataMin', 'dataMax']} scale="time" tick={{ fontSize: 10, fill: 'rgb(var(--chu-2))' }}
                tickFormatter={(v) => (khoang === '24h' ? new Date(v).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' }) : new Date(v).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' }))} />
              <YAxis yAxisId="p" tick={{ fontSize: 10, fill: 'rgb(var(--chu-2))' }} />
              <YAxis yAxisId="d" orientation="right" domain={[0, 100]} tick={{ fontSize: 10, fill: 'rgb(var(--chu-2))' }} />
              <Tooltip labelFormatter={(v) => new Date(Number(v)).toLocaleString('vi-VN')} {...KIEU_GOI_Y} />
              <Area yAxisId="d" type="stepAfter" dataKey="d" name="%" stroke="rgb(var(--tim))" fill={toMien(idMien, 1)} fillOpacity={0.5} strokeWidth={1.5} connectNulls={false} isAnimationActive={false} />
              <Area yAxisId="p" type="monotone" dataKey="p" name="P (W)" stroke="rgb(var(--nhan))" fill={toMien(idMien, 0)} fillOpacity={1} strokeWidth={2} connectNulls={false} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        ) : <p className="m-0 grid h-full place-items-center text-sm text-chu-3">{q.isLoading ? 'Đang tải…' : 'Chưa có số đo'}</p>}
      </div>
    </div>
  );
}

export function ODen({ t, addr }: { t: TuCay; addr: number }) {
  const tuQ = useNemaTu(t.id);
  const nhomQ = useNhomTu(t.id);
  const d = tuQ.data?.den.find((x) => x.addr === addr) ?? null;
  const dc = t.den.find((x) => x.addr === addr) ?? null;
  const nhom = d?.groupId ? nhomQ.data?.find((g) => g.id === d.groupId)?.name ?? '—' : 'Mặc định';
  const gps = d && d.gps.lat_e7 !== null && d.gps.lon_e7 !== null ? `${(d.gps.lat_e7 / 1e7).toFixed(5)}, ${(d.gps.lon_e7 / 1e7).toFixed(5)}` : '—';
  const b = dc ? NHAN_BADGE[dc.badge] : null;
  return (
    <section className="flex flex-col gap-3 rounded-the border border-vien bg-nen-1 p-4" data-o="o-den">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="m-0 min-w-0 truncate text-lg font-semibold text-chu-1">{dc ? tenDen(dc) : 'Đèn'}</h2>
        {b && <Badge tone={b.tone}>{b.nhan}</Badge>}
        <span className="text-sm text-chu-2">{t.name}</span>
      </div>
      <Nhom ten="Định danh">
        <O nhan="ID" gia={hex4(addr)} />
        <O nhan="Nhóm" gia={nhom} />
        <O nhan="Công suất danh định" gia={so(d?.rated_w, 'W')} />
        <O nhan="Vị trí bản đồ" gia={d?.pos ? `${d.pos.lat.toFixed(5)}, ${d.pos.lng.toFixed(5)}` : '—'} />
      </Nhom>
      <Nhom ten="Hoạt động">
        <O nhan="Độ sáng" gia={so(d?.dim, '%')} />
        <O nhan="P" gia={so(d?.p_w, 'W')} />
        <O nhan="U" gia={so(d?.u_v, 'V')} />
        <O nhan="I" gia={so(iMa(d?.i_10ma), 'mA')} />
        <O nhan="PF" gia={pfHai(d?.pf_pct) ?? '—'} />
        <O nhan="GPS đèn" gia={gps} />
      </Nhom>
      <Nhom ten="Cài đặt">
        <O nhan="Tên" gia={d?.name?.trim() || '—'} />
        <O nhan="Cập nhật" gia={tuoiNgan(d?.sensor_at ?? null)} />
      </Nhom>
      <DoThiDen cabinetId={t.id} addr={addr} />
      <div className="flex flex-wrap items-center gap-2" data-o="nut-dot-e">
        <Button size="sm" variant="secondary" disabled>Thêm đèn</Button>
        <Button size="sm" variant="secondary" disabled>Thay đèn</Button>
        <Button size="sm" variant="secondary" disabled>Xoá</Button>
        <span className="text-xs text-chu-3">làm ở đợt E</span>
      </div>
    </section>
  );
}
